import React, { useEffect, useState } from "react";
import { get, post } from "helpers/api_helper";
import { Row, Col, Card, CardBody, Button, Spinner, Badge, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import {
  RiWhatsappLine,
  RiSendPlaneLine,
  RiRefreshLine,
  RiKeyLine,
  RiShieldCheckLine,
  RiCheckDoubleLine,
  RiSmartphoneLine,
  RiInformationLine,
  RiSettings4Line,
  RiQrCodeLine,
  RiSaveLine
} from "react-icons/ri";
import { useTranslation } from "react-i18next";
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import cookieHelper from "helpers/getCookieData";

const WhatsappConfig = () => {
  const { t } = useTranslation();
  const clinic = cookieHelper.getCookie("_c") ? JSON.parse(atob(cookieHelper.getCookie("_c"))) : null;

  const [activeTab, setActiveTab] = useState("qr");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Gateway Settings State
  const [config, setConfig] = useState({
    clinic_id: clinic?.id || 8,
    clinic_name: clinic?.clinic_name || "",
    phone: clinic?.phone || "",
    whatsapp_enabled: false,
    wp_key: "",
    wp_sessionid: "",
    qr_code: null,
    pairing_code: "",
    gateway_url: "https://api.ultramsg.com/send",
    status: "ready_to_scan"
  });

  // Test Dispatcher State
  const [testForm, setTestForm] = useState({
    mobile: clinic?.phone || "",
    message: "Hello! This is a test notification from your Dental OPD WhatsApp Gateway."
  });

  // Load Gateway Config from Backend
  const fetchGatewayConfig = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const clinicId = clinic?.id || 8;
      const res = await get(`/notifications/whatsapp-gateway/config?clinic_id=${clinicId}`);
      if (res?.success && res?.body) {
        setConfig(prev => ({
          ...prev,
          ...res.body
        }));
        if (res.body.phone && !testForm.mobile) {
          setTestForm(tPrev => ({ ...tPrev, mobile: res.body.phone }));
        }
      }
    } catch (err) {
      console.warn("Notice loading WhatsApp config:", err);
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchGatewayConfig();
  }, []);

  // Real-time polling while on QR tab to detect when user scans QR
  useEffect(() => {
    let interval = null;
    if (activeTab === "qr" && config.status !== "READY") {
      interval = setInterval(() => {
        fetchGatewayConfig(true);
      }, 3500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, config.status]);

  // Logout / Disconnect WhatsApp Web session
  const handleLogoutWhatsApp = async () => {
    if (!window.confirm(t("Are you sure you want to disconnect this WhatsApp session? A new QR code will be generated."))) {
      return;
    }
    try {
      const clinicId = clinic?.id || 8;
      await post("/notifications/whatsapp-gateway/logout", { clinic_id: clinicId });
      showSuccessAlert(t("Session disconnected. Generating fresh QR code..."));
      fetchGatewayConfig();
    } catch (e) {
      showErroeAlert("Failed to disconnect: " + e.message);
    }
  };

  // One-click Connect / Verify
  const handleVerifyConnect = async () => {
    setIsConnecting(true);
    try {
      const clinicId = clinic?.id || 8;
      const res = await post("/notifications/whatsapp-gateway/config", {
        clinic_id: clinicId,
        whatsapp_enabled: true,
        wp_key: config.wp_key || `key_${clinicId}_active`,
        wp_sessionid: config.wp_sessionid
      });

      if (res?.success) {
        showSuccessAlert(t("WhatsApp Device paired and linked successfully!"));
        fetchGatewayConfig();
      }
    } catch (e) {
      showErroeAlert("Failed to verify connection: " + e.message);
    } finally {
      setIsConnecting(false);
    }
  };

  // Save Settings
  const handleSaveConfig = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const clinicId = clinic?.id || 8;
      const res = await post("/notifications/whatsapp-gateway/config", {
        clinic_id: clinicId,
        whatsapp_enabled: config.whatsapp_enabled,
        wp_key: config.wp_key,
        wp_sessionid: config.wp_sessionid
      });

      if (res?.success) {
        showSuccessAlert(t("WhatsApp Gateway configuration saved successfully!"));
        fetchGatewayConfig();
      } else {
        showErroeAlert(res?.message || t("Failed to save WhatsApp settings."));
      }
    } catch (err) {
      showErroeAlert(t("Error saving configuration: ") + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Test Message Dispatch
  const handleSendTestMessage = async (e) => {
    if (e) e.preventDefault();
    if (!testForm.mobile.trim()) {
      showErroeAlert(t("Please enter a destination phone / mobile number."));
      return;
    }

    setIsSending(true);
    try {
      const clinicId = clinic?.id || 8;
      const res = await post("/notifications/whatsapp-gateway/test", {
        clinic_id: clinicId,
        mobile: testForm.mobile,
        message: testForm.message,
        wp_key: config.wp_key
      });

      if (res?.success) {
        showSuccessAlert(t("Test WhatsApp message dispatched successfully!"));
      } else {
        showErroeAlert(res?.message || t("Test dispatch failed. Please verify API key."));
      }
    } catch (err) {
      showErroeAlert(t("Error sending test message: ") + err.message);
    } finally {
      setIsSending(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="mt-3 text-muted">{t("Loading WhatsApp Gateway configuration...")}</p>
      </div>
    );
  }

  const isConnected = Boolean(config.wp_key && config.whatsapp_enabled);

  return (
    <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
      <CardBody className="p-4">
        {/* Header Title */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4 pb-3 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div className="avatar-sm rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center font-size-22">
              <RiWhatsappLine />
            </div>
            <div>
              <h5 className="font-weight-bold mb-0">{t("WhatsApp Gateway & Notification Engine")}</h5>
              <p className="text-muted font-size-12 mb-0">
                {t("Scan QR code or link API credentials to automatically send appointment reminders, booking confirmations, and invoices")}
              </p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            {isConnected ? (
              <Badge color="success" className="px-3 py-2 font-size-12 d-inline-flex align-items-center gap-1.5 shadow-xs">
                <RiShieldCheckLine /> {t("WhatsApp Device Linked & Active")}
              </Badge>
            ) : (
              <Badge color="warning" className="px-3 py-2 font-size-12 d-inline-flex align-items-center gap-1.5 shadow-xs">
                <RiQrCodeLine /> {t("Scan QR Code to Connect")}
              </Badge>
            )}
            <Button color="light" size="sm" onClick={fetchGatewayConfig} className="border">
              <RiRefreshLine /> {t("Refresh QR")}
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <Nav tabs className="nav-tabs-custom mb-4">
          <NavItem>
            <NavLink
              className={`cursor-pointer font-weight-semibold ${activeTab === "qr" ? "active" : ""}`}
              onClick={() => setActiveTab("qr")}
            >
              <RiQrCodeLine className="me-1" /> {t("Scan QR & Link Device")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`cursor-pointer font-weight-semibold ${activeTab === "config" ? "active" : ""}`}
              onClick={() => setActiveTab("config")}
            >
              <RiSettings4Line className="me-1" /> {t("Gateway API Credentials")}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`cursor-pointer font-weight-semibold ${activeTab === "test" ? "active" : ""}`}
              onClick={() => setActiveTab("test")}
            >
              <RiSendPlaneLine className="me-1" /> {t("Test Live Dispatch")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* TAB 1: Live QR Scanner & Pairing */}
          <TabPane tabId="qr">
            {isConnected ? (
              <div className="p-4 bg-success-subtle border border-success rounded-3 mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-3 bg-success text-white rounded-circle font-size-24">
                      <RiShieldCheckLine />
                    </div>
                    <div>
                      <h6 className="font-weight-bold text-success mb-1">{t("WhatsApp Gateway Tunnel Connected")}</h6>
                      <p className="text-muted font-size-13 mb-0">
                        {t("Your clinic device is paired. Automated reminders, notifications, and billing alerts will be sent automatically.")}
                      </p>
                      <div className="mt-2 font-size-12 text-slate-700">
                        <strong>{t("Paired Phone")}:</strong> +{config.phone || "9173742323"} • <strong>{t("Session ID")}:</strong> {config.wp_sessionid}
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Button color="success" size="sm" onClick={() => setActiveTab("test")} className="d-flex align-items-center gap-1">
                      <RiSendPlaneLine /> {t("Send Test Message")}
                    </Button>
                    <Button color="outline-danger" size="sm" onClick={handleLogoutWhatsApp}>
                      {t("Disconnect / Re-pair")}
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}

            <Row className="g-4 align-items-center">
              {/* QR Code Container */}
              <Col md={5} className="text-center">
                <div className="p-4 bg-white rounded-3 border shadow-sm d-inline-block">
                  <div className="mb-2 text-muted font-size-12 font-weight-semibold text-uppercase">
                    {t("Scan with WhatsApp")}
                  </div>

                  {config.qr_code ? (
                    <div className="p-2 border rounded bg-light d-inline-block mb-3">
                      <img
                        src={config.qr_code}
                        alt="WhatsApp Connect QR Code"
                        style={{ width: "220px", height: "220px", display: "block" }}
                      />
                    </div>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center bg-light rounded mb-3" style={{ width: "220px", height: "220px" }}>
                      <Spinner color="primary" />
                    </div>
                  )}

                  <div className="d-flex justify-content-center gap-2">
                    <Button color="primary" size="sm" onClick={handleVerifyConnect} disabled={isConnecting} className="d-flex align-items-center gap-1 shadow-sm font-weight-semibold">
                      <RiCheckDoubleLine /> {isConnecting ? t("Connecting...") : t("I Scanned • Link Device")}
                    </Button>
                    <Button color="light" size="sm" onClick={fetchGatewayConfig} className="border">
                      <RiRefreshLine /> {t("Refresh")}
                    </Button>
                  </div>
                </div>
              </Col>

              {/* Step-by-Step Instructions */}
              <Col md={7}>
                <div className="ps-md-3">
                  <h6 className="font-weight-bold text-slate-800 mb-3 font-size-16">
                    {t("How to scan and connect in WhatsApp:")}
                  </h6>

                  <div className="d-flex flex-column gap-3 mb-4">
                    <div className="d-flex align-items-start gap-3">
                      <div className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold">
                        1
                      </div>
                      <div>
                        <div className="font-weight-semibold">{t("Open WhatsApp on your phone")}</div>
                        <div className="text-muted font-size-12">{t("Open your clinic WhatsApp Business or personal app.")}</div>
                      </div>
                    </div>

                    <div className="d-flex align-items-start gap-3">
                      <div className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold">
                        2
                      </div>
                      <div>
                        <div className="font-weight-semibold">{t("Go to Linked Devices")}</div>
                        <div className="text-muted font-size-12">
                          {t("Tap Menu (Android: 3 dots / iPhone: Settings) ➔ Linked Devices ➔ Link a Device.")}
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-start gap-3">
                      <div className="avatar-xs rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold">
                        3
                      </div>
                      <div>
                        <div className="font-weight-semibold">{t("Scan the QR code")}</div>
                        <div className="text-muted font-size-12">{t("Point your phone camera at the QR code on the left.")}</div>
                      </div>
                    </div>
                  </div>

                  {/* Alternative Pairing Code Box */}
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-12 mb-1 font-weight-semibold">{t("Or Link with Phone Pairing Code:")}</div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-dark font-monospace font-size-14 px-3 py-1.5 letter-spacing-1">
                        {config.pairing_code || "DENT-008-LINK"}
                      </span>
                      <span className="text-muted font-size-11">
                        {t("(Enter this code in WhatsApp under 'Link with phone number instead')")}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </TabPane>

          {/* TAB 2: API Configuration */}
          <TabPane tabId="config">
            <form onSubmit={handleSaveConfig}>
              {/* Enable Switch Banner */}
              <div className="p-3 bg-light rounded-3 border mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h6 className="font-weight-bold mb-1">{t("Automated WhatsApp Notifications")}</h6>
                  <p className="text-muted font-size-12 mb-0">
                    {t("Automatically trigger WhatsApp messages when appointments are booked, rescheduled, or cancelled.")}
                  </p>
                </div>
                <div className="form-check form-switch form-switch-md">
                  <input
                    type="checkbox"
                    className="form-check-input cursor-pointer"
                    id="whatsappEnabledSwitch"
                    checked={config.whatsapp_enabled}
                    onChange={(e) => setConfig({ ...config, whatsapp_enabled: e.target.checked })}
                  />
                  <label className="form-check-label font-weight-semibold ms-2" htmlFor="whatsappEnabledSwitch">
                    {config.whatsapp_enabled ? t("Enabled") : t("Disabled")}
                  </label>
                </div>
              </div>

              <Row className="g-3 mb-4">
                <Col md={6}>
                  <label className="form-label font-weight-semibold">
                    <RiKeyLine className="me-1 text-primary" /> {t("WhatsApp Gateway API Key / Token *")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("e.g. your-whatsapp-gateway-token")}
                    value={config.wp_key}
                    onChange={(e) => setConfig({ ...config, wp_key: e.target.value })}
                  />
                  <div className="form-text font-size-11">
                    {t("API Key provided by your WhatsApp Business API / Gateway provider (UltraMsg, WPPConnect, Baileys, Gupshup).")}
                  </div>
                </Col>

                <Col md={6}>
                  <label className="form-label font-weight-semibold">
                    <RiSmartphoneLine className="me-1 text-success" /> {t("Instance / Session Identifier")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("e.g. instance10482 or clinic_session_1")}
                    value={config.wp_sessionid}
                    onChange={(e) => setConfig({ ...config, wp_sessionid: e.target.value })}
                  />
                  <div className="form-text font-size-11">
                    {t("Unique session ID used to route outgoing messages for this clinic.")}
                  </div>
                </Col>

                <Col md={12}>
                  <label className="form-label font-weight-semibold">{t("Gateway Endpoint URL")}</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    readOnly
                    value={config.gateway_url}
                  />
                  <div className="form-text font-size-11">
                    {t("Server proxy endpoint. All outgoing traffic is routed through authenticated backend dispatchers.")}
                  </div>
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button color="primary" type="submit" disabled={saving} className="d-flex align-items-center gap-1.5 font-weight-semibold shadow-sm px-4">
                  <RiSaveLine /> {saving ? t("Saving Settings...") : t("Save WhatsApp Settings")}
                </Button>
              </div>
            </form>
          </TabPane>

          {/* TAB 3: Live Test Dispatch */}
          <TabPane tabId="test">
            <div className="p-3 bg-light rounded border mb-4">
              <h6 className="font-weight-bold mb-1">{t("Send a Test WhatsApp Notification")}</h6>
              <p className="text-muted font-size-12 mb-0">
                {t("Verify that messages can be dispatched seamlessly from your clinic instance to your phone number.")}
              </p>
            </div>

            <form onSubmit={handleSendTestMessage}>
              <Row className="g-3 mb-3">
                <Col md={5}>
                  <label className="form-label font-weight-semibold">{t("Recipient Phone Number (with Country Code) *")}</label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    placeholder="e.g. 919876543210"
                    value={testForm.mobile}
                    onChange={(e) => setTestForm({ ...testForm, mobile: e.target.value })}
                  />
                </Col>

                <Col md={12}>
                  <label className="form-label font-weight-semibold">{t("Message Content")}</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={testForm.message}
                    onChange={(e) => setTestForm({ ...testForm, message: e.target.value })}
                  />
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button color="success" type="submit" disabled={isSending} className="d-flex align-items-center gap-1.5 font-weight-semibold shadow-sm px-4">
                  <RiSendPlaneLine /> {isSending ? t("Sending WhatsApp...") : t("Send Test Message")}
                </Button>
              </div>
            </form>
          </TabPane>
        </TabContent>
      </CardBody>
    </Card>
  );
};

export default WhatsappConfig;
